import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Pencil1Icon, PersonIcon, TrashIcon } from '@radix-ui/react-icons';

import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { updateCharacterCover } from '../character.slice';
import { CoverImage } from '../../../components/cover-image/cover-image';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { Person } from '../../../components/icons/person';
import { catchError } from '../../../core/catch-error';
import { FormBuilderModal } from '../../../components/form-builder-modal/form-builder-modal';
import { buildCharacterForm } from '../character-form.definition';
import { formatPicture } from '../../../core/services/image.service';
import { Project } from '../../../app/entities/project';

import type { Character } from '../../../app/entities/character';
import type { Frame } from '../../../lib/utils/transform-image';
import { openModal } from '../../../core/modal/open-modal';

import styles from './character-item.module.scss';

type CharacterProps = {
  character: Character;
};

export const CharacterItem: React.FC<CharacterProps> = observer(({ character }) => {
  const { name } = character;
  const navigate = useNavigate();
  const location = useLocation();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updateModalKey, setUpdateModalKey] = useState(character?._id);

  const isCharactersRoute = location.pathname.includes('/characters'.toLowerCase());

  const handleCharacterNavigation = (e: any) => {
    e.stopPropagation();
    navigate(`./${character?._id}`);
  };

  const handleCandidatesNavigation = (e: any) => {
    e.stopPropagation();

    if (!isCharactersRoute) {
      navigate(`./${character?._id}`);
      return;
    }

    const projectId = character.projectId;
    const project = Project.getOne(projectId);
    if (project) {
      const castingStep = project.steps.find((el) => el.type === 'casting');
      if (castingStep) {
        navigate(`../steps/${castingStep._id}/${character?._id}`);
      }
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await character.delete();
    } catch (e) {
      catchError(e);
    }
  };

  const handleEditCover = async (file: Frame) => {
    try {
      await updateCharacterCover({ characterId: character._id, file });
    } catch (e) {
      catchError(e);
    }
  };

  const handleEdit = async (data: Partial<Character>) => {
    await character.update(data);
    // To rerender the update form modal
    setUpdateModalKey(character._id + Math.random());
  };

  const handleUploadPhoto = async (file?: File) => {
    if (!file) {
      return;
    }
    const options = { width: 340, height: 340 };
    const frame = await formatPicture(file, options);
    await updateCharacterCover({ characterId: character._id, file: frame });
  };

  const menuItems = [
    {
      onSelect: () => setIsEditModalOpen(true),
      title: 'Edit',
      icon: <Pencil1Icon />,
    },
    {
      onSelect: handleCandidatesNavigation,
      title: 'Manage candidates',
      icon: <PersonIcon />,
    },
    {
      onSelect: () =>
        openModal(DeleteDialog, {
          onSubmit: handleConfirmDelete,
          title: 'Delete character',
          text: `Are you sure you want to delete ${name} ?`,
        }),
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
    },
  ];

  const updateCharacterFormDefinition = buildCharacterForm(false, handleUploadPhoto);

  return (
    <>
      <div className={styles['container']} onClick={handleCharacterNavigation}>
        <div className={styles['header']}>
          <h3 className={styles['name']}>{name}</h3>

          <div className={styles['actions-container']}>
            <button className={styles['cast-list-button']} onClick={handleCandidatesNavigation}>
              <span>{character.candidates.length}</span>
              <Person />
            </button>

            <DropdownMenu items={menuItems} size="large" />
          </div>
        </div>

        <div className={styles['character-image-container']}>
          <CoverImage
            src={character.cover?.src || ''}
            title={character.name}
            onEdit={handleEditCover}
          />
        </div>

        <div className={styles['additional-info-block']}>
          <span className={styles['additional-info-label']}>Selected Candidate</span>
          <h6 className={styles['selected-candidate-name']}>{character.castName}</h6>
          <p className={styles['selected-candidate-title']}>{character.cast && 'Cast'}</p>
        </div>

        {/* TODO: Handle Requirements */}
        <div className={styles['additional-info-block']}>
          <span className={styles['additional-info-label']}>Description</span>
          <h3 className={styles['requirements']}>{character.description}</h3>
        </div>
      </div>

      {isEditModalOpen && (
        <FormBuilderModal
          key={updateModalKey}
          model="character"
          definition={updateCharacterFormDefinition}
          initialValues={{ ...character, avatar: character.cover?.src }}
          onSubmit={handleEdit}
          onCancel={() => setIsEditModalOpen(false)}
        />
      )}
    </>
  );
});
